import type { ApiHookReturn, FirstPurchaseData } from '@frontend/api-client';
import { calculateAmountDueToday } from '@frontend/api-client';
import type { PromotionDetails } from '@frontend/api-types';
import type { MutationHandler, StrictExclude } from '@frontend/duck-tape';
import {
  P,
  coalesceToUndefined,
  convertDateLikeToDateISOString,
  dayjs,
  formatCurrencyNatural,
  isAPICustomError,
  isAPIValidationError,
  match,
  pluralize,
} from '@frontend/duck-tape';
import { useCallback, useEffect, usePrevious, useRef, useState } from '@frontend/react';
import { usePromoSlice } from '@frontend/redux';
import { normalizePromoCode } from './normalizePromoCode';
import { useIsLinkedInPromo } from './useIsLinkedInPromo';

type UsePromotionTranslationProps = {
  defaultPromoCode?: string;
  firstPurchaseData?: MaybeUndefined<FirstPurchaseData>;
  handleMutation: MutationHandler;
  retrievePromotionDetailsHookResult: ApiHookReturn<'useLazyRetrievePromotionDetailsQuery'>;
};

export type PromotionTranslationState = {
  amountDueToday: MaybeUndefined<Cent>;
  amountOff: MaybeUndefined<StrictExclude<PromotionDetails['amountOff'], null>>;
  durationInMonths: MaybeUndefined<StrictExclude<PromotionDetails['durationInMonths'], null>>;
  fixedPriceCents: MaybeUndefined<StrictExclude<PromotionDetails['fixedPriceCents'], null>>;
  freeTasksCap: MaybeUndefined<StrictExclude<PromotionDetails['freeTasksCap'], null>>;
  hasAppliedPromoCode: boolean;
  hasSuccessfullyAppliedPromoCode: boolean;
  isApplyPromoCodeLoading: boolean;
  isFirstTimeOnly: MaybeUndefined<StrictExclude<PromotionDetails['isFirstTimeOnly'], null>>;
  isLinkedInPromo: boolean;
  nextBillingDate: MaybeUndefined<DateISOString>;
  percentOff: MaybeUndefined<StrictExclude<PromotionDetails['percentOff'], null>>;
  promoCode: MaybeUndefined<string>;
  promoCodeErrorMessage: MaybeUndefined<string>;
  promotionDescription: MaybeUndefined<string>;
  promotionDetails: MaybeUndefined<PromotionDetails>;
  promotionName: MaybeUndefined<string>;
};

export type PromotionTranslationActions = {
  handleApplyPromoCode: (promoCode: string) => Promise<MaybeUndefined<PromotionDetails>>;
  resetPromoCode: () => void;
  setPromoCodeErrorMessage: (promoCodeErrorMessage: string) => void;
};

export type UsePromotionTranslationReturn = {
  actions: PromotionTranslationActions;
  state: PromotionTranslationState;
};

const INVALID_PROMO_CODE_ERROR_MESSAGE = 'This promo code is invalid. Please try another code.';
const RETRIABLE_PROMO_CODE_ERROR_MESSAGE = 'We could not apply this promo code right now. Please try again.';

const isInvalidPromoCodeError = (error: unknown) => {
  if (isAPIValidationError(error)) {
    return true;
  }

  if (isAPICustomError(error)) {
    return error.status === 400 || error.status === 404 || error.status === 422;
  }

  return false;
};

export const getPromotionDescription = (promotionDetails: PromotionDetails): string => {
  const { amountOff, durationInMonths, fixedPriceCents, freeTasksCap, isCreateSubscriptionOnSignup, percentOff } =
    promotionDetails;

  const afterText = isCreateSubscriptionOnSignup ? "You'll also" : "After that you'll";
  const effectiveAmountFreeCredits = freeTasksCap ?? null;

  return match({
    amountOff,
    countFreeCredits: effectiveAmountFreeCredits,
    durationInMonths,
    fixedPriceCents,
    percentOff,
  })
    .with(
      {
        amountOff: P.number.positive(),
        countFreeCredits: P.number.positive(),
        durationInMonths: P.number.positive(),
        percentOff: P.number.positive(),
      },
      ({ amountOff, countFreeCredits, durationInMonths, percentOff }) =>
        `${countFreeCredits} credits. ${afterText} get ${percentOff}% off for ${durationInMonths} ${pluralize('month', durationInMonths)} and save ${formatCurrencyNatural(amountOff)} per month`,
    )
    .with(
      {
        countFreeCredits: P.number.positive(),
        durationInMonths: P.number.positive(),
        fixedPriceCents: P.number.positive(),
        percentOff: P.number.positive(),
      },
      ({ countFreeCredits, durationInMonths, fixedPriceCents, percentOff: _percentOff }) =>
        `${countFreeCredits} credits. For the first ${getPromotionDurationText(durationInMonths)}, you'll only pay ${formatCurrencyNatural(fixedPriceCents)}`,
    )

    .with(
      { amountOff: P.number.positive(), durationInMonths: P.number.positive(), percentOff: P.number.positive() },
      ({ amountOff, durationInMonths, percentOff }) =>
        `${percentOff}% off for ${durationInMonths} ${pluralize('month', durationInMonths)} and save ${formatCurrencyNatural(amountOff)} per month`,
    )
    .with(
      {
        amountOff: P.number.positive(),
        countFreeCredits: P.number.positive(),
        durationInMonths: P.number.positive(),
      },
      ({ amountOff, countFreeCredits, durationInMonths }) =>
        `${countFreeCredits} credits. ${afterText} save ${formatCurrencyNatural(amountOff)} for ${durationInMonths} ${pluralize('month', durationInMonths)}`,
    )
    .with(
      {
        countFreeCredits: P.number.positive(),
        durationInMonths: P.number.positive(),
        percentOff: P.number.positive(),
      },
      ({ countFreeCredits, durationInMonths, percentOff }) =>
        `${countFreeCredits} credits. ${afterText} get ${percentOff}% off for ${durationInMonths} ${pluralize('month', durationInMonths)}`,
    )
    .with(
      {
        countFreeCredits: P.number.positive(),
        durationInMonths: P.number.positive(),
        fixedPriceCents: P.number.positive(),
      },
      ({ countFreeCredits, durationInMonths, fixedPriceCents }) =>
        `${countFreeCredits} credits. For the first ${getPromotionDurationText(durationInMonths)}, you'll only pay ${formatCurrencyNatural(fixedPriceCents)}`,
    )

    .with(
      { amountOff: P.number.positive(), durationInMonths: P.number.positive() },
      ({ amountOff, durationInMonths }) =>
        `Save ${formatCurrencyNatural(amountOff)} for your first ${getPromotionDurationText(durationInMonths)}`,
    )
    .with(
      { durationInMonths: P.number.positive(), percentOff: P.number.positive() },
      ({ durationInMonths, percentOff }) =>
        `${percentOff}% off for your first ${getPromotionDurationText(durationInMonths)}`,
    )
    .with(
      { durationInMonths: P.number.positive(), fixedPriceCents: P.number.positive() },
      ({ durationInMonths, fixedPriceCents }) =>
        `For the first ${getPromotionDurationText(durationInMonths)}, you'll only pay ${formatCurrencyNatural(fixedPriceCents)}`,
    )
    .with({ countFreeCredits: P.number.positive() }, ({ countFreeCredits }) => `${countFreeCredits} credits`)
    .otherwise(() => 'This promotion has no discount details.');
};

const getPromotionDurationText = (durationInMonths: number) => {
  if (durationInMonths === 1) {
    return 'month';
  }
  return `${durationInMonths} months`;
};

export const usePromotionTranslation = ({
  defaultPromoCode,
  firstPurchaseData,
  handleMutation,
  retrievePromotionDetailsHookResult,
}: UsePromotionTranslationProps): UsePromotionTranslationReturn => {
  const [retrievePromotionDetails, { isLoading: isApplyPromoCodeLoading }] = retrievePromotionDetailsHookResult;

  const {
    clearPromo: clearReduxPromo,
    setPromoCode: setReduxPromoCode,
    setPromoDetails: setReduxPromoDetails,
  } = usePromoSlice();

  const [promoCodeErrorMessage, setPromoCodeErrorMessage] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState<string>();
  const [promotionDetails, setPromoDetails] = useState<PromotionDetails>();
  const [canAutoApplyPromoCode, setCanAutoApplyPromoCode] = useState(true);
  const activePromoRequestTokenRef = useRef(0);
  const prevSelectedPurchaseType = usePrevious(firstPurchaseData?.selectedPurchaseType);
  const isLinkedInPromo = useIsLinkedInPromo();

  const resetPromoCode = useCallback(() => {
    // eslint-disable-next-line fp/no-mutation -- Ref mutation invalidates older promo requests.
    activePromoRequestTokenRef.current += 1;
    setAppliedPromoCode(undefined);
    setPromoDetails(undefined);
    setPromoCodeErrorMessage('');
    clearReduxPromo();
  }, [clearReduxPromo]);

  const applyPromoCode = useCallback(
    async (promoCode: string) => {
      const normalizedPromoCode = normalizePromoCode(promoCode);

      if (!normalizedPromoCode) {
        return { isInvalidPromoCode: false, promotionDetails: undefined };
      }

      // eslint-disable-next-line fp/no-mutation -- Ref mutation establishes latest-request-wins ordering.
      activePromoRequestTokenRef.current += 1;
      const promoRequestToken = activePromoRequestTokenRef.current;

      setAppliedPromoCode(normalizedPromoCode);

      try {
        const promotionDetails = await handleMutation({
          mutation: async () => retrievePromotionDetails(normalizedPromoCode).unwrap(),
          onError: (error) => {
            if (activePromoRequestTokenRef.current !== promoRequestToken) {
              throw new Error('stalePromoCodeRequest');
            }

            const invalidPromoCode = isInvalidPromoCodeError(error);
            const errorMessage = invalidPromoCode
              ? INVALID_PROMO_CODE_ERROR_MESSAGE
              : RETRIABLE_PROMO_CODE_ERROR_MESSAGE;

            setPromoDetails(undefined);
            setPromoCodeErrorMessage(errorMessage);
            clearReduxPromo();

            throw new Error(invalidPromoCode ? 'invalidPromoCode' : 'retriablePromoCode');
          },
          onSuccess: (promoDetails) => {
            if (activePromoRequestTokenRef.current !== promoRequestToken) {
              return;
            }

            setPromoDetails(promoDetails);
            setPromoCodeErrorMessage('');
            setReduxPromoCode(normalizedPromoCode);
            setReduxPromoDetails(promoDetails);
          },
          showErrorToast: false,
          showSuccessToast: false,
        });

        if (activePromoRequestTokenRef.current !== promoRequestToken) {
          return { isInvalidPromoCode: false, promotionDetails: undefined };
        }

        return { isInvalidPromoCode: false, promotionDetails };
      } catch (error) {
        if (error instanceof Error && error.message === 'stalePromoCodeRequest') {
          return { isInvalidPromoCode: false, promotionDetails: undefined };
        }

        return {
          isInvalidPromoCode: error instanceof Error && error.message === 'invalidPromoCode',
          promotionDetails: undefined,
        };
      }
    },
    [clearReduxPromo, handleMutation, retrievePromotionDetails, setReduxPromoCode, setReduxPromoDetails],
  );

  const handleApplyPromoCode = useCallback(
    async (promoCode: string) => {
      const result = await applyPromoCode(promoCode);

      return result.promotionDetails;
    },
    [applyPromoCode],
  );

  useEffect(() => {
    if (prevSelectedPurchaseType === 'Subscription' && firstPurchaseData?.selectedPurchaseType === 'ALaCarteCredits') {
      resetPromoCode();
      setCanAutoApplyPromoCode(true);
    }
  }, [firstPurchaseData?.selectedPurchaseType, prevSelectedPurchaseType, resetPromoCode]);

  useEffect(() => {
    if (defaultPromoCode && !appliedPromoCode && canAutoApplyPromoCode) {
      setCanAutoApplyPromoCode(false);

      applyPromoCode(defaultPromoCode).then(({ isInvalidPromoCode, promotionDetails }) => {
        if (!promotionDetails && isInvalidPromoCode) {
          resetPromoCode();
        }
      });
    }
  }, [appliedPromoCode, applyPromoCode, canAutoApplyPromoCode, defaultPromoCode, resetPromoCode]);

  const promotionName =
    promotionDetails?.displayLabel ?? (appliedPromoCode ? `Promo code: ${appliedPromoCode}` : undefined);

  const promotionDescription = promotionDetails ? getPromotionDescription(promotionDetails) : undefined;
  const { amountOff, durationInMonths, fixedPriceCents, freeTasksCap, isFirstTimeOnly, percentOff } = {
    amountOff: coalesceToUndefined(promotionDetails?.amountOff),
    durationInMonths: coalesceToUndefined(promotionDetails?.durationInMonths),
    fixedPriceCents: coalesceToUndefined(promotionDetails?.fixedPriceCents),
    freeTasksCap: coalesceToUndefined(promotionDetails?.freeTasksCap),
    isFirstTimeOnly: coalesceToUndefined(promotionDetails?.isFirstTimeOnly),
    percentOff: coalesceToUndefined(promotionDetails?.percentOff),
  };

  const amountDueToday = calculateAmountDueToday({ firstPurchaseData, promotionDetails });

  const nextBillingDate = match(firstPurchaseData?.selectedPurchaseType)
    .with('Subscription', () =>
      promotionDetails?.isCreateSubscriptionOnSignup
        ? convertDateLikeToDateISOString(dayjs().add(1, 'month'))
        : undefined,
    )
    .with('ALaCarteCredits', () => undefined)
    .otherwise(() => undefined);

  const hasAppliedPromoCode = Boolean(promoCodeErrorMessage || promotionDescription);

  const hasSuccessfullyAppliedPromoCode = Boolean(!promoCodeErrorMessage && promotionDetails);

  return {
    actions: {
      handleApplyPromoCode,
      resetPromoCode,
      setPromoCodeErrorMessage,
    },
    state: {
      amountDueToday,
      amountOff,
      durationInMonths,
      fixedPriceCents,
      freeTasksCap,
      hasAppliedPromoCode,
      hasSuccessfullyAppliedPromoCode,
      isApplyPromoCodeLoading,
      isFirstTimeOnly,
      isLinkedInPromo,
      nextBillingDate,
      percentOff,
      promoCode: promotionDetails ? appliedPromoCode : undefined,
      promoCodeErrorMessage,
      promotionDescription,
      promotionDetails,
      promotionName,
    },
  } satisfies UsePromotionTranslationReturn;
};
